import { useState, useMemo, useCallback, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import Sidebar from "../../components/Sidebar";
import TopBar from "../../components/TopBar";
import useIsMobile from "../../components/shared/Mobile-Responsive";
import IMAGES from "../../../../constant/images";
import { Button, Select, Input, DatePicker, Modal } from "antd";
import AppointmentsCounter from "../../components/Appointments/AppointmentsCounter";
import AppointmentsTable from "../../components/Appointments/AppointmentsTable";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Filter,
  ArrowUpDown,
  ChevronRight,
  X,
  CalendarDays,
  CalendarClock,
  CalendarCheck,
  AlertCircle,
  CalendarX,
} from "lucide-react";
import { getRiskStatusBadge, getStatusColor, getStatusText, getRiskText, getRiskColor, getRiskTextColor, transformFiltersForAPI } from "../../../../utils/index";
import { getAppointmentStats, getAppointmentsList } from "../../api/Appointments/index";
import AppointmentFilterSlider from "../../components/Appointments/AppointmentFilterSlider";
import ExportModal from "../../components/shared/ExportModal";
import api from "../../../../redux/instance/api";
import dayjs from "dayjs";

// Icon mapping for counter data
const COUNTER_ICONS = {
  total: IMAGES.completed_icon,
  upcoming: IMAGES.practitioner_icon,
  completed: IMAGES.completed_icon,
  incomplete: IMAGES.practitioner_icon,
  cancelled: IMAGES.completed_icon
};

// Mapping stat card IDs to API filter values (matching AppointmentsControl dropdown)
const STAT_TO_FILTER_MAP = {
  'total': 'all',
  'upcoming': 'UPCOMING',     // Maps to dropdown value (not SCHEDULED)
  'completed': 'COMPLETED',   // Maps to API value
  'incomplete': 'IN_COMPLETE', // Maps to API value
  'cancelled': 'CANCELED'     // Maps to API value (note: CANCELED not CANCELLED)
};

// Function to get counter data from API stats
const getCounterDataFromStats = (statsData) => {
  const statsConfig = [
    { key: 'total', dataKey: 'totalAppointments', label: 'Total' },
    { key: 'upcoming', dataKey: 'upcomingAppointments', label: 'Upcoming' },
    { key: 'completed', dataKey: 'completedAppointments', label: 'Completed' },
    { key: 'incomplete', dataKey: 'incompleteAppointments', label: 'Incomplete' },
    { key: 'cancelled', dataKey: 'cancelledAppointments', label: 'Cancelled' }
  ];

  return statsConfig.map(config => ({
    icon: COUNTER_ICONS[config.key],
    count: statsData?.[config.dataKey]?.toString() || "0",
    label: config.label
  }));
};

const { Option } = Select;

const StatusBadge = ({ status, statusColor }) => {
  return (
    <div className="flex items-center gap-1">
      <div className={`w-3 h-3 rounded-full ${statusColor}`}></div>
      <span className="text-[#4B61FF] text-sm font-semibold font-poppins">
        {status}
      </span>
    </div>
  );
};

StatusBadge.propTypes = {
  status: PropTypes.string.isRequired,
  statusColor: PropTypes.string.isRequired
};

const RiskBadge = ({ riskStatus }) => {
  const badge = getRiskStatusBadge(riskStatus);
  return (
    <div
      className="rounded-full px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.5px] font-poppins"
      style={{
        backgroundColor: badge.backgroundColor,
        color: badge.color
      }}
    >
      {badge.text}
    </div>
  );
};

RiskBadge.propTypes = {
  riskStatus: PropTypes.string.isRequired
};

const MobileAppointmentCard = ({ appointment, onViewDetails }) => {
  return (
    <div className="bg-white border-[1px] border-solid border-[#E2E8F0] rounded-2xl shadow-sm p-3 mb-4 relative font-poppins">
      {/* Checkbox */}
      <div className="absolute top-4 right-4">
        <input type="checkbox" className="w-5 h-5 rounded accent-[#4B61FF]" />
      </div>

      <div className="flex gap-8">
        {/* Labels Column */}
        <div className="flex flex-col gap-3 text-[11px] leading-5 font-semibold text-[#64748B] uppercase tracking-wide">
          <div>TYPE</div>
          <div>DATE</div>
          <div>TIME SLOT</div>
          <div>STATUS</div>
          <div>MODE</div>
          <div>PRACTITIONER</div>
          <div>MEMBER NAME</div>
          <div>RISK</div>
        </div>

        {/* Values Column */}
        <div className="flex flex-col gap-3 flex-1">
          <div className="text-sm font-semibold text-[#171717]">{appointment.type}</div>
          <div className="text-sm text-[#334155]">{appointment.date}</div>
          <div className="text-sm text-[#334155]">{appointment.timeSlot}</div>
          <StatusBadge status={appointment.status} statusColor={appointment.statusColor} />
          <div className="text-sm text-[#334155]">{appointment.mode}</div>
          <div className="text-sm text-[#334155]">{appointment.practitioner}</div>
          <div className="text-sm text-[#334155]">{appointment.memberName}</div>
          <RiskBadge riskStatus={appointment.originalRisk} />
        </div>
      </div>

      {/* Separator */}
      <div className="h-px bg-[#F1F5F9] my-4" />

      {/* View Details Button */}
      <div className="flex justify-end">
        <button
          onClick={() => onViewDetails(appointment.id)}
          className="flex items-center justify-center w-[26px] h-[26px] rounded-full bg-[#F1F5F9] hover:bg-[#E2E8F0] border-0 transition-colors"
        >
          <ChevronRight size={14} className="text-[#64748B]" />
        </button>
      </div>
    </div>
  );
};

MobileAppointmentCard.propTypes = {
  appointment: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    timeSlot: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    statusColor: PropTypes.string.isRequired,
    mode: PropTypes.string.isRequired,
    practitioner: PropTypes.string.isRequired,
    memberName: PropTypes.string.isRequired,
    originalRisk: PropTypes.string.isRequired
  }).isRequired,
  onViewDetails: PropTypes.func.isRequired
};

// Presentational lucide icon + tone per stat id (same as AppointmentsCounter)
const COUNTER_LUCIDE = {
  total: { Icon: CalendarDays, tone: 'bg-[#EFF6FF] text-[#2563EB]' },
  upcoming: { Icon: CalendarClock, tone: 'bg-[#EFF6FF] text-[#2563EB]' },
  completed: { Icon: CalendarCheck, tone: 'bg-[#F0FDFA] text-[#0D9488]' },
  incomplete: { Icon: AlertCircle, tone: 'bg-[#FFF7ED] text-[#EA580C]' },
  cancelled: { Icon: CalendarX, tone: 'bg-[#FFF1F2] text-[#E11D48]' }
};

const MobileCounterCard = ({ icon, count, label, onClick, isSelected, statId }) => {
  const lucide = COUNTER_LUCIDE[statId];
  return (
    <div
      className={`bg-white border-[1px] border-solid rounded-2xl p-3 min-w-[140px] flex-shrink-0 cursor-pointer shadow-sm hover:shadow-md transition-all duration-300 ease-in-out font-poppins ${
        isSelected ? 'border-[#2563EB] ring-2 ring-[#BFDBFE]' : 'border-[#E2E8F0]'
      }`}
      onClick={() => onClick && onClick(statId)}
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={`Filter by ${label.toLowerCase()} appointments`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick && onClick(statId);
        }
      }}
    >
      <div className="flex flex-col">
        <div className={`flex items-center justify-center w-[38px] h-[38px] rounded-xl flex-shrink-0 mb-1.5 ${lucide ? lucide.tone : 'bg-[#EFF6FF]'}`}>
          {lucide ? <lucide.Icon size={18} /> : <img src={icon} alt={label} className="w-5 h-5" />}
        </div>
        <div className="text-[22px] leading-tight font-bold text-[#171717]">{count}</div>
        <div className="mt-0.5 text-[12px] uppercase tracking-wide font-medium text-[#64748B]">{label}</div>
      </div>
    </div>
  );
};

MobileCounterCard.propTypes = {
  icon: PropTypes.string.isRequired,
  count: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  isSelected: PropTypes.bool,
  statId: PropTypes.string
};

export default function Index() {
  const [collapsed, setCollapsed] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useIsMobile(768);

  // Mobile-specific state
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedDate, setSelectedDate] = useState(null);
  const [sortBy, setSortBy] = useState("date");
  const [sortOrder, setSortOrder] = useState("desc");
  const [appliedFilters, setAppliedFilters] = useState(null);

  // Stat card selection state
  const [selectedStatId, setSelectedStatId] = useState(null);

  // Export state
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportData, setExportData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);

  // Redux state
  const { data: appointmentStats, loading: statsLoading } = useSelector(state => state.appointments.stats);
  const {
    data: appointmentsList,
    loading: listLoading,
    totalElements,
    totalPages,
    currentPage: apiCurrentPage
  } = useSelector(state => state.appointments.list);

  // Handle stat card click
  const handleStatCardClick = useCallback((statId) => {
    if (selectedStatId === statId) {
      // Deselect if clicking the same stat
      setSelectedStatId(null);
      setSelectedStatus('all');
    } else {
      // Select new stat and update status filter
      setSelectedStatId(statId);
      const filterValue = STAT_TO_FILTER_MAP[statId];
      setSelectedStatus(filterValue);
    }
    setCurrentPage(1); // Reset to first page when filtering
  }, [selectedStatId]);

  // Load appointment statistics and list on mount and filter changes
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      dispatch(getAppointmentStats());

      // Build filters for API call
      const combinedFilters = {};

      // Add status filter if not 'all'
      if (selectedStatus && selectedStatus !== 'all') {
        combinedFilters.status = selectedStatus;
      }

      // Add date filter
      if (selectedDate) {
        const filterDate = dayjs(selectedDate).format('YYYY-MM-DD');
        combinedFilters.startDate = filterDate;
        combinedFilters.endDate = filterDate;
      }

      // Add search text
      if (searchValue) {
        combinedFilters.practitionerName = searchValue;
        combinedFilters.memberName = searchValue;
      }

      // Add advanced filters using utility function
      if (appliedFilters) {
        const transformedFilters = transformFiltersForAPI(appliedFilters);
        Object.assign(combinedFilters, transformedFilters);
      }

      dispatch(getAppointmentsList({
        page: currentPage - 1, // API uses 0-based indexing
        size: pageSize,
        filters: combinedFilters
      }));
    }
  }, [dispatch, currentPage, pageSize, selectedStatus, selectedDate, searchValue, appliedFilters]);

  // Get counter data for mobile view
  const counterData = getCounterDataFromStats(appointmentStats);
  
  // Transform API data for mobile view (filtering/sorting now handled by API)
  const transformedAppointmentsData = useMemo(() => {
    if (!appointmentsList || !Array.isArray(appointmentsList)) return [];

    return appointmentsList.map((item, index) => ({
      key: item.appointmentId || index + 1,
      id: item.appointmentId,
      type: item.appointmentType,
      date: item.appointmentDate,
      timeSlot: item.timeSlot,
      status: getStatusText(item.status),
      originalStatus: item.status,
      mode: item.mode,
      practitioner: item.practitionerName,
      memberName: item.memberName,
      risk: getRiskText(item.riskStatus),
      originalRisk: item.riskStatus,
      statusColor: getStatusColor(item.status),
      riskColor: getRiskColor(item.riskStatus),
      riskTextColor: getRiskTextColor(item.riskStatus)
    }));
  }, [appointmentsList]);
  
  // Pagination data from API (no need for local slicing since API handles pagination)
  const paginationData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalElements);

    return {
      totalItems: totalElements || 0,
      startIndex,
      endIndex,
      currentPageData: transformedAppointmentsData, // API already returns paginated data
      totalPages: totalPages || 0
    };
  }, [transformedAppointmentsData, currentPage, pageSize, totalElements, totalPages]);

  const handleViewDetails = useCallback((appointmentId) => {
    navigate(`/appointment-detail/${appointmentId}`);
  }, [navigate]);

  const fetchAllAppointments = useCallback(async () => {
    const combinedFilters = {};

    // Add status filter if not 'all'
    if (selectedStatus && selectedStatus !== 'all') {
      combinedFilters.status = selectedStatus;
    }

    // Add date filter
    if (selectedDate) {
      const filterDate = dayjs(selectedDate).format('YYYY-MM-DD');
      combinedFilters.startDate = filterDate;
      combinedFilters.endDate = filterDate;
    }

    // Add search text
    if (searchValue) {
      combinedFilters.practitionerName = searchValue;
      combinedFilters.memberName = searchValue;
    }

    // Add advanced filters using utility function
    if (appliedFilters) {
      const transformedFilters = transformFiltersForAPI(appliedFilters);
      Object.assign(combinedFilters, transformedFilters);
    }

    const params = new URLSearchParams({
      page: "0",
      size: "5000"
    });

    if (combinedFilters.startDate) params.append('startDate', combinedFilters.startDate);
    if (combinedFilters.endDate) params.append('endDate', combinedFilters.endDate);

    const FILTER_PARAMS = ['appointmentType', 'status', 'mode', 'practitionerName', 'memberName', 'riskStatus', 'name'];
    FILTER_PARAMS.forEach(filterKey => {
      if (combinedFilters[filterKey] !== undefined && combinedFilters[filterKey] !== null) {
        if (combinedFilters[filterKey].includes(',')) {
          combinedFilters[filterKey].split(',').forEach(value =>
            params.append(filterKey, value.trim())
          );
        } else {
          params.append(filterKey, combinedFilters[filterKey]);
        }
      }
    });

    const response = await api.get(`/admin/appointments-list?${params.toString()}`);
    const list = response.data?.content || [];

    return list.map((item, index) => ({
      key: item.appointmentId || index + 1,
      id: item.appointmentId,
      type: item.appointmentType,
      date: item.appointmentDate,
      timeSlot: item.timeSlot,
      status: getStatusText(item.status),
      originalStatus: item.status,
      mode: item.mode,
      practitioner: item.practitionerName,
      memberName: item.memberName,
      risk: getRiskText(item.riskStatus),
      originalRisk: item.riskStatus,
      statusColor: getStatusColor(item.status),
      riskColor: getRiskColor(item.riskStatus),
      riskTextColor: getRiskTextColor(item.riskStatus)
    }));
  }, [selectedStatus, selectedDate, searchValue, appliedFilters]);

  const handleExportClick = useCallback(() => {
    setExportModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setExportModalVisible(false);
  }, []);

  const handleExportData = useCallback((format) => {
    console.log(`Exporting appointment data as ${format}`);
    setExportModalVisible(false);
  }, []);

  const handleCreateNew = useCallback(() => {
    navigate("/add-appointments");
  }, [navigate]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  // Mobile filter and search handlers
  const handleSearchToggle = useCallback(() => {
    setIsSearchOpen(!isSearchOpen);
  }, [isSearchOpen]);

  const handleFilterToggle = useCallback(() => {
    setIsFilterOpen(!isFilterOpen);
  }, [isFilterOpen]);

  const handleSortToggle = useCallback(() => {
    setIsSortOpen(!isSortOpen);
  }, [isSortOpen]);

  const handleSearchChange = useCallback((e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1);
  }, []);

  const handleSearchSubmit = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  const handleFilterApply = useCallback((filters) => {
    setAppliedFilters(filters);
    setCurrentPage(1);
  }, []);

  const handleFilterClear = useCallback(() => {
    setAppliedFilters(null);
    setCurrentPage(1);
  }, []);

  const handleStatusFilter = useCallback((status) => {
    setSelectedStatus(status);
    setCurrentPage(1);
  }, []);

  const handleDateFilter = useCallback((date) => {
    setSelectedDate(date);
    setCurrentPage(1);
  }, []);

  const handleSortChange = useCallback((field, order) => {
    setSortBy(field);
    setSortOrder(order);
    setIsSortOpen(false);
    setCurrentPage(1);
  }, []);

  if (isMobile) {
    return (
      <>
        <TopBar />
        <div className="bg-white min-h-screen pt-16 font-poppins">
          {/* Content */}
          <div className="px-4 py-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-[#171717] font-poppins mb-0">Appointments</h1>
            <div className="flex items-center gap-6">
              <button
                onClick={handleExportClick}
                className="flex items-center gap-2 h-9 px-[14px] bg-white text-[#171717] text-[13px] font-semibold border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition"
              >
                <span>Export</span>
                <img src={IMAGES.export_icon} alt="Export" className="w-4 h-4" />
              </button>
              {/* <button
                onClick={handleCreateNew}
                className="bg-[#0B4F9F] rounded-md p-2"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <div className="text-white text-lg font-bold">+</div>
                </div>
              </button> */}
            </div>
          </div>

          {/* Counter Cards - Horizontal Scroll */}
          <div className="overflow-x-auto mb-4">
            <div className="flex gap-3 p-1 pb-4">
              {counterData.map((item, index) => {
                const statId = Object.keys(COUNTER_ICONS)[index]; // Map index to stat ID
                return (
                  <MobileCounterCard
                    key={`counter-${index}`}
                    {...item}
                    statId={statId}
                    onClick={handleStatCardClick}
                    isSelected={selectedStatId === statId}
                  />
                );
              })}
            </div>
          </div>

          {/* Separator */}
          <div className="h-px bg-[#F1F5F9] mb-4" />

          {/* All Appointments Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-[#171717] mb-0">All Appointments</h2>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSearchToggle}
                className="flex items-center justify-center w-9 h-9 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition-colors"
              >
                <Search size={18} className="text-[#2563EB]" />
              </button>
              <button
                onClick={handleFilterToggle}
                className="flex items-center justify-center w-9 h-9 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition-colors"
              >
                <Filter size={18} className="text-[#2563EB]" />
              </button>
              <button
                onClick={handleSortToggle}
                className="flex items-center justify-center w-9 h-9 bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] transition-colors"
              >
                <ArrowUpDown size={18} className="text-[#2563EB]" />
              </button>
            </div>
          </div>

          {/* Appointment Cards */}
          <div className="mb-6">
            {listLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563EB] mx-auto"></div>
                <p className="mt-2 text-[#64748B]">Loading appointments...</p>
              </div>
            ) : paginationData.currentPageData.length > 0 ? (
              paginationData.currentPageData.map((appointment) => (
                <MobileAppointmentCard
                  key={appointment.key}
                  appointment={appointment}
                  onViewDetails={handleViewDetails}
                />
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-[#64748B]">No appointments found</p>
              </div>
            )}
          </div>

          {/* Pagination */}
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#64748B]">Show on page</span>
                <Select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="min-w-[90px]"
                  size="small"
                >
                  <Option value={5}>5 Rows</Option>
                  <Option value={10}>10 Rows</Option>
                  <Option value={20}>20 Rows</Option>
                  <Option value={30}>30 Rows</Option>
                </Select>
              </div>
              <div className="text-xs text-[#64748B]">
                Showing {paginationData.startIndex + 1} to {paginationData.endIndex} of {paginationData.totalItems} entries
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button 
                className="px-5 py-2.5 text-xs font-semibold text-[#334155] bg-white border-0 disabled:opacity-50"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
              >
                Previous
              </button>
              
              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(paginationData.totalPages, 3) }, (_, i) => {
                  let pageNum;
                  if (paginationData.totalPages <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage <= 2) {
                    pageNum = i + 1;
                  } else if (currentPage >= paginationData.totalPages - 1) {
                    pageNum = paginationData.totalPages - 2 + i;
                  } else {
                    pageNum = currentPage - 1 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`min-w-[32px] h-8 px-2.5 rounded-lg text-xs font-semibold ${
                        currentPage === pageNum
                          ? "bg-[#0F172A] text-white border-0"
                          : "bg-white border-[1px] border-solid border-[#E2E8F0] text-[#334155]"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button 
                className="px-5 py-2.5 text-xs font-semibold text-[#334155] bg-white border-0 disabled:opacity-50"
                disabled={currentPage === paginationData.totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
              >
                Next
              </button>
            </div>
          </div>

          {/* Search Modal */}
          <Modal
            open={isSearchOpen}
            onCancel={() => setIsSearchOpen(false)}
            footer={null}
            title={null}
            centered
            className="mobile-search-modal"
            styles={{
              content: { padding: 0, borderRadius: '16px' },
              body: { padding: 0 }
            }}
          >
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-base font-semibold text-[#171717] font-poppins">Search Appointments</h3>
              </div>

              <div className="space-y-4">
                <Input
                  placeholder="Search by Member Name / Practitioner Name"
                  value={searchValue}
                  onChange={handleSearchChange}
                  className="h-12 rounded-xl border-[#E2E8F0] font-poppins"
                  prefix={<Search className="w-4 h-4 text-[#94A3B8]" />}
                  autoFocus
                />

                <div className="flex gap-3">
                  <Button
                    onClick={() => {
                      setSearchValue('');
                    }}
                    className="flex-1 h-12 rounded-xl border-[#E2E8F0] text-[#334155] font-semibold font-poppins"
                  >
                    Clear
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSearchSubmit}
                    className="flex-1 h-12 rounded-xl font-semibold font-poppins"
                  >
                    Search
                  </Button>
                </div>
              </div>
            </div>
          </Modal>

          {/* Sort Modal */}
          <Modal
            open={isSortOpen}
            onCancel={() => setIsSortOpen(false)}
            footer={null}
            title={null}
            centered
            className="mobile-sort-modal"
            styles={{
              content: { padding: 0, borderRadius: '16px' },
              body: { padding: 0 }
            }}
          >
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-base font-semibold text-[#171717] font-poppins">Sort Appointments</h3>
              </div>

              <div className="space-y-3">
                {[
                  { key: 'date', label: 'Date' },
                  { key: 'practitioner', label: 'Practitioner Name' },
                  { key: 'member', label: 'Member Name' },
                  { key: 'status', label: 'Status' }
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-2">
                    <div className="text-sm font-medium text-[#334155] font-poppins">{label}</div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleSortChange(key, 'asc')}
                        className={`flex-1 px-4 py-2 rounded-xl border-[1px] border-solid text-sm font-semibold font-poppins transition ${
                          sortBy === key && sortOrder === 'asc'
                            ? 'bg-[#E8F2FF] border-[#B3D4FF] text-[#4B61FF]'
                            : 'bg-[#F8FAFC] text-[#334155] border-[#E2E8F0] hover:bg-gray-100'
                        }`}
                      >
                        Ascending
                      </button>
                      <button
                        onClick={() => handleSortChange(key, 'desc')}
                        className={`flex-1 px-4 py-2 rounded-xl border-[1px] border-solid text-sm font-semibold font-poppins transition ${
                          sortBy === key && sortOrder === 'desc'
                            ? 'bg-[#E8F2FF] border-[#B3D4FF] text-[#4B61FF]'
                            : 'bg-[#F8FAFC] text-[#334155] border-[#E2E8F0] hover:bg-gray-100'
                        }`}
                      >
                        Descending
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Modal>

          {/* Filter Slider */}
          <AppointmentFilterSlider
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            onApplyFilters={handleFilterApply}
            onClearAll={handleFilterClear}
          />

          {/* Export Modal */}
          <ExportModal
            visible={exportModalVisible}
            onClose={handleCloseModal}
            onExport={handleExportData}
            selectedRows={selectedRows}
            data={transformedAppointmentsData}
            onFetchAllData={fetchAllAppointments}
          />
          </div>
        </div>
      </>
    );
  }

  // Desktop View (existing implementation)
  return (
    <div className="fixed inset-x-0 bottom-0 top-16 flex bg-[#F8FAFC] overflow-hidden">
      <TopBar />

      {/* Sidebar */}
      <div
        className={`hidden lg:flex ${collapsed ? "w-[64px]" : "w-[272px]"} fixed top-0 left-0 h-screen z-20`}
      >
        <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 min-w-0 overflow-auto bg-[#F8FAFC] pt-2 px-2 pb-8 transition-all duration-300 ${
          collapsed ? "lg:ml-[64px]" : "lg:ml-[272px]"
        } max-lg:ml-0`}
      >
        <div className="p-4 md:p-6 bg-[#FFFFFF] min-h-screen rounded-2xl max-lg:rounded-none w-full">
          {/* Header */}
          <div className="max-w-full mx-auto p-2 mb-1 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
            <h1 className="text-xl font-bold text-[#171717] font-poppins mb-0">Appointments</h1>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button
                onClick={handleExportClick}
                className="flex items-center justify-center gap-2 h-9 px-[14px] py-0 bg-white text-[#171717] text-[13px] font-semibold font-poppins border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm hover:bg-[#F8FAFC] hover:text-[#171717] transition flex-1 sm:flex-initial"
              >
                Export
                <img
                  src={IMAGES.export_icon}
                  alt="export_icon"
                  className="w-[16px]"
                />
              </Button>
              {/* <Button
                onClick={() => navigate("/add-appointments")}
                className="flex items-center gap-2 px-4 py-2 text-base font-semibold border border-[#B3C8E1] rounded-lg h-10 bg-[#0B4F9F] text-white hover:bg-[#B3C8E1] hover:text-white transition-colors duration-300"
              >
                <img
                  src={IMAGES.create_followup}
                  alt="export_icon"
                  className="w-5"
                />
                Create New
              </Button> */}
            </div>
          </div>

          {/* Counter */}
          <div className="p-2">
            <AppointmentsCounter
              onStatClick={handleStatCardClick}
              selectedStatId={selectedStatId}
            />
          </div>

          {/* Separator */}
          <div className="mx-2 mt-1 mb-2 border-t-[1px] border-solid border-x-0 border-b-0 border-[#F1F5F9]"></div>

          {/* Table */}
          <div className="overflow-x-auto">
            <AppointmentsTable externalStatusFilter={selectedStatus} />
          </div>
        </div>
      </div>

      {/* Export Modal */}
      <ExportModal
        visible={exportModalVisible}
        onClose={handleCloseModal}
        onExport={handleExportData}
        selectedRows={selectedRows}
        data={transformedAppointmentsData}
        onFetchAllData={fetchAllAppointments}
      />
    </div>
  );
}